import { createAction } from "@reduxjs/toolkit";
import { consoleWarn } from "common/utilities/browser/consoleLog/consoleLog";
import * as API from "core/api/api";
import { isAutoCompletionResultResponse, isPageResponse, type ResponseTypes } from "core/api/api.typesGuards";
import { NO_RESPONSE_MESSAGE, StatusCode } from "core/api/apiHelpers";
import type * as I from "core/api/coreApiClient";
import { isFetchError, isResponse, isResponseJSONParsingError } from "core/api/openApi.typesGuards";
import type { pageLoadingFailed } from "core/redux/actions/page/pageActions";
import type { AppThunk, AppThunkDispatch } from "core/redux/store/configureStore.types";
import type { EnhancedCommand } from "core/utilities/core.types";
import { isError } from "core/utilities/exceptionHandler/exceptionHandler";
import ExceptionLogger from "core/utilities/ExceptionLogger/ExceptionLogger";

import type {
  CommandDeleteActionOutput,
  CommandGetActionOutput,
  CommandResponseTypes,
  CommandSaveActionOutput,
  CommandUpdateActionOutput,
} from "./commandActions.types";

export interface CommandTriggerContext {
  sectionId?: string;
}

/**
 * Generic action sent when a command fails with an error (JS error) or responseError (fetch error)
 */
export const commandFailed = (
  errorMsg: string,
  error?: Error,
  errorResponse?: {
    status: number;
    data?: ResponseTypes;
  }
) => ({
  payload: {
    errorMsg,
    error,
    errorResponse,
  },
});

/**
 * Handle JS errors that may occur (within redux actions/reducers/middlewares) before an API call is made:
 * - create an incident
 * - show the incident ID within a notification
 * - console warn the error
 *
 * Note: this does not log fetch related errors, those are caught and logged via the loggerMiddleware
 */
export const handleCommandError = async (
  error: unknown,
  commandName: string,
  dispatch: AppThunkDispatch,
  failedAction:
    | typeof commandUpdateFailed
    | typeof commandSaveFailed
    | typeof commandDeleteFailed
    | typeof commandGetFailed
    | typeof pageLoadingFailed
) => {
  // JS errors
  if (isError(error) && !isFetchError(error) && !isResponseJSONParsingError(error)) {
    const payload = await ExceptionLogger.log(error, undefined, [`error incident within ${commandName}`]);
    consoleWarn(error.stack || []);

    if (payload) {
      dispatch(failedAction(payload?.incidentId || "INCIDENT", error, undefined));
    }

    return true;
  }

  // No error / Fetch error / JSON missing
  if (!error || isFetchError(error) || isResponseJSONParsingError(error)) {
    dispatch(failedAction(NO_RESPONSE_MESSAGE));
  }

  return false;
};

export const startCommand = createAction(
  "command/startCommand",
  (command: I.Command, data?: unknown, context?: CommandTriggerContext) => ({
    payload: {
      command,
      data,
      context,
    },
  })
);

export const commandGetSuccess = createAction("command/commandGetSuccess", (response: CommandResponseTypes) => ({
  payload: {
    response,
  },
}));

export const commandGetFailed = createAction("command/commandGetFailed", commandFailed);

export const getCommand =
  (command: I.Command, retry?: boolean): AppThunk<CommandGetActionOutput> =>
  async (dispatch) => {
    try {
      dispatch(startCommand(command));

      const url = command.url || "";

      const response = (await API.commandGet(url, retry)) as CommandResponseTypes;
      dispatch(commandGetSuccess(response));
      return await Promise.resolve(response);
    } catch (error) {
      if (await handleCommandError(error, "getCommand", dispatch, commandGetFailed)) {
        return Promise.resolve(undefined);
      }

      const errorResponse = error;

      if (isResponse(errorResponse)) {
        dispatch(commandGetFailed(errorResponse.statusText, undefined, { status: errorResponse.status }));
      }

      return Promise.resolve(undefined);
    }
  };

export const commandUpdateSuccess = createAction("command/commandUpdateSuccess");

export const commandUpdateFailed = createAction("command/commandUpdateFailed", commandFailed);

export const updateCommand =
  <T>(command: I.Command, data: T, context?: CommandTriggerContext): AppThunk<CommandUpdateActionOutput> =>
  async (dispatch) => {
    try {
      dispatch(startCommand(command, data, context));

      const url = command.url || "";

      const response = await API.commandUpdate(url, data);
      dispatch(commandUpdateSuccess());
      return await Promise.resolve(response);
    } catch (error) {
      if (await handleCommandError(error, "updateCommand", dispatch, commandUpdateFailed)) {
        return Promise.resolve(undefined);
      }

      if (!error || isError(error)) {
        return Promise.reject(error);
      }

      const errorResponse = error;

      if (isResponse(errorResponse)) {
        let responseData: ResponseTypes | undefined;

        try {
          responseData = await errorResponse.json();
        } catch (e) {
          // Unauthorized or 404 etc. calls don't always return data
        }

        if (errorResponse.status === StatusCode.NOT_FOUND && isAutoCompletionResultResponse(responseData)) {
          return Promise.resolve(responseData);
        }

        dispatch(
          commandUpdateFailed(errorResponse.statusText, undefined, { status: errorResponse.status, data: responseData })
        );

        if (errorResponse.status === 422 && isPageResponse(responseData)) {
          return Promise.resolve(responseData);
        }
      }

      return Promise.resolve(undefined);
    }
  };

export const commandSaveSuccess = createAction("command/commandSaveSuccess", (page?: I.Page) => ({
  payload: { page },
}));

export const commandSaveFailed = createAction("command/commandSaveFailed", commandFailed);

export const saveCommand =
  <T>(command: I.Command, data: T): AppThunk<CommandSaveActionOutput> =>
  async (dispatch) => {
    try {
      dispatch(startCommand(command, data));

      const url = command.url || "";

      const response = await API.commandSave(url, data);
      dispatch(commandSaveSuccess(isPageResponse(response) ? response.payload : undefined));
      return await Promise.resolve(response);
    } catch (error) {
      if (await handleCommandError(error, "saveCommand", dispatch, commandSaveFailed)) {
        return Promise.resolve(undefined);
      }

      if (!error || isError(error)) {
        return Promise.reject(error);
      }

      const errorResponse = error;

      if (isResponse(errorResponse)) {
        let responseData: ResponseTypes | undefined;

        try {
          responseData = await errorResponse.json();
        } catch (e) {
          // Unauthorized or 404 etc. calls don't always return data
        }

        dispatch(
          commandSaveFailed(errorResponse.statusText, undefined, { status: errorResponse.status, data: responseData })
        );
      }

      return Promise.resolve(undefined);
    }
  };

export const commandDeleteSuccess = createAction("command/commandDeleteSuccess");

export const commandDeleteFailed = createAction("command/commandDeleteFailed", commandFailed);

export const deleteCommand =
  (command: I.Command): AppThunk<CommandDeleteActionOutput> =>
  async (dispatch) => {
    try {
      dispatch(startCommand(command));

      const url = command.url || "";

      const response = await API.commandDelete(url);
      dispatch(commandDeleteSuccess());
      return await Promise.resolve(response);
    } catch (error) {
      if (await handleCommandError(error, "deleteCommand", dispatch, commandDeleteFailed)) {
        return Promise.resolve(undefined);
      }

      if (!error || isError(error)) {
        return Promise.reject(error);
      }

      const errorResponse = error;

      let responseData: ResponseTypes | undefined;

      if (isResponse(errorResponse)) {
        try {
          responseData = await errorResponse.json();
        } catch (e) {
          // Unauthorized or 404 etc. calls don't always return data
        }

        dispatch(
          commandDeleteFailed(errorResponse.statusText, undefined, { status: errorResponse.status, data: responseData })
        );
      }

      return Promise.resolve(undefined);
    }
  };

export const replaceCommand = createAction(
  "command/replaceCommand",
  (groupId: string, command: EnhancedCommand, newCommands: EnhancedCommand[]) => ({
    payload: {
      groupId,
      command,
      newCommands,
    },
  })
);
